import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react'
import {
  type MarketMatch, API_ROUTES, MARKETS_MAX_LIMIT, MARKETS_PAGE_SIZE, type MarketGroup, type MarketsResponse, type SlipsMineResponse, type ZambeBetMatchResponse, type ZambeBetOutrightsResponse, type ZambeBetQuotesResponse,
} from '@brochure/shared'
import { apiFetch } from '@/lib/api'
import { remember, resourceKey, sameGuest } from '@/lib/recent'
import { matchKey, matchStore, nextSeq, walletKey, walletStore, type Wallet } from '@/lib/wallet-store'
import { useLive } from './useLive'

/** Ultime risposte per chiave (percorso + token): tornando su una pagina già vista i dati ci sono subito. */
const RECENT_MAX = 20
const recent = new Map<string, unknown>()

/**
 * GET di una risorsa, riletta quando cambiano `path`, `guestToken` o `version` (contatore dei messaggi live).
 * I dati precedenti restano visibili durante la rilettura: niente sfarfallio a ogni bicchiere. Una chiave già vista
 * parte dall'ultima risposta (stale-while-revalidate); `previous` è l'ultima arrivata per un'altra chiave dello
 * stesso ospite (mai di un altro: niente portafoglio altrui), per tenere la pagina in piedi mentre arriva la nuova
 * (altra pagina del palinsesto, altro gruppo di mercati) o se la nuova fallisce.
 */
function useApiResource<T>(path: string | null, guestToken: string | undefined, version: number, onFresh?: (data: T, seq: number) => void) {
  // In un ref: una funzione nuova a ogni render non deve far ripartire la lettura.
  const fresh = useRef(onFresh)
  fresh.current = onFresh
  const key = path === null ? null : resourceKey(path, guestToken)
  // `last`: l'ultima risposta riuscita, con la sua chiave; resta anche quando una lettura successiva fallisce.
  const [state, setState] = useState<{ key: string | null; data?: T; error?: unknown; last?: { key: string; data: T } }>({ key: null })
  const [reloads, setReloads] = useState(0)

  useEffect(() => {
    if (path === null) return
    const controller = new AbortController()
    // Numerata alla partenza: il saldo che porta vale solo se nessuna richiesta partita dopo ha già risposto.
    const seq = nextSeq()
    apiFetch<T>(path, { signal: controller.signal, guestToken })
      .then((data) => {
        if (key === null) return
        fresh.current?.(data, seq)
        remember(recent, key, data, RECENT_MAX)
        setState({ key, data, last: { key, data } })
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) setState((previous) => ({ key, data: previous.key === key ? previous.data : undefined, error, last: previous.last }))
      })
    return () => controller.abort()
  }, [path, guestToken, key, version, reloads])

  const isCurrent = state.key === key
  const cached = key === null ? undefined : (recent.get(key) as T | undefined)
  return {
    data: (isCurrent ? state.data : undefined) ?? cached,
    previous: key !== null && state.last && sameGuest(state.last.key, key) ? state.last.data : undefined,
    error: isCurrent ? state.error : undefined,
    /** Risposta per la chiave attuale non ancora arrivata (anche se `data` viene dalla cache). */
    isLoading: !isCurrent,
    reload: useCallback(() => setReloads((n) => n + 1), []),
  }
}

/**
 * Il saldo dell'ospite dall'ultima risposta arrivata di qualunque pagina ZambeBet del torneo (`lib/wallet-store.ts`),
 * non dalla copia in cache della pagina aperta: cambiando pagina o gruppo non si rivede il saldo vecchio.
 * `undefined` finché nessuna risposta è arrivata (le pagine usano allora quello della loro risposta).
 */
export function useWallet(tournamentId: string | undefined, guestToken: string | undefined): Wallet | undefined {
  const key = tournamentId ? walletKey(tournamentId, guestToken) : null
  return useSyncExternalStore(walletStore.subscribe, () => (key === null ? undefined : walletStore.get(key)))
}

/**
 * Lo stato più recente di una partita (punteggio, stato, 1-2) dall'ultima risposta arrivata di qualunque pagina,
 * non dalla copia in cache della pagina aperta: cambiando pagina o gruppo non si rivede il risultato vecchio.
 * Restituisce una funzione che sostituisce la partita della risposta con quella più fresca, se c'è.
 */
export function useFreshMatch(tournamentId: string | undefined, guestToken: string | undefined): (m: MarketMatch) => MarketMatch {
  // La funzione cambia solo quando cambia una partita (`version`): chi la usa in `useMemo` non ricalcola a ogni render.
  const version = useSyncExternalStore(matchStore.subscribe, matchStore.getVersion)
  return useCallback(
    (m: MarketMatch) => (tournamentId ? matchStore.get(matchKey(tournamentId, guestToken, m.matchId)) : undefined) ?? m,
    [tournamentId, guestToken, version],
  )
}

/** Registra saldo e partite di una risposta fresca nei negozi unici (`lib/wallet-store.ts`). */
function remembered(tournamentId: string | undefined, guestToken: string | undefined) {
  return (data: { guest: Wallet; matches?: MarketMatch[]; match?: MarketMatch }, seq: number) => {
    if (!tournamentId) return
    walletStore.offer(walletKey(tournamentId, guestToken), seq, data.guest)
    for (const m of data.matches ?? (data.match ? [data.match] : [])) matchStore.offer(matchKey(tournamentId, guestToken, m.matchId), seq, m)
  }
}

/** Quote degli eventi in schedina (`legKey`): un solo GET per tutti, ripetuto agli stessi messaggi live. */
const quotesPath = (base: string | null, legKeys: readonly string[]) =>
  base && legKeys.length > 0 ? `${base}/zambebet/quotes?matches=${[...legKeys].sort().join(',')}` : null

export type ZambeBetTab = 'markets' | 'outrights' | 'mine'

/**
 * Dati di ZambeBet per un torneo, riletti a ogni evento live con lo stesso `limit`:
 * - palinsesto con le quote (le prime `limit` partite da giocare; `loadMore` ne aggiunge una pagina);
 * - antepost;
 * - schedine dell'ospite;
 * - quote degli eventi in schedina.
 * Ogni tab chiede solo il suo: l'Antepost non chiede il palinsesto, il palinsesto non fa simulare il torneo.
 */
export function useZambeBet(tournamentId: string | undefined, guestToken: string | undefined, slipLegKeys: string[] = [], tab: ZambeBetTab = 'markets') {
  const [version, setVersion] = useState(0)
  const [limit, setLimit] = useState(MARKETS_PAGE_SIZE)
  // Anche il timer fa rileggere: avviarlo o fermarlo cambia le quote.
  const { isOpen } = useLive(tournamentId, () => setVersion((v) => v + 1))
  const base = tournamentId ? `${API_ROUTES.tournaments}/${tournamentId}` : null
  const fresh = remembered(tournamentId, guestToken)
  const markets = useApiResource<MarketsResponse>(base && tab !== 'outrights' ? `${base}/markets?limit=${limit}` : null, guestToken, version, fresh)
  const outrights = useApiResource<ZambeBetOutrightsResponse>(base && tab === 'outrights' ? `${base}/zambebet/outrights` : null, guestToken, version, fresh)
  const slipQuotes = useApiResource<ZambeBetQuotesResponse>(quotesPath(base, slipLegKeys), undefined, version)
  const mySlips = useApiResource<SlipsMineResponse>(base && guestToken ? `${base}/slips/mine` : null, guestToken, version)
  // Mai oltre il massimo del server (400 altrimenti): lì "Carica altro" sparisce.
  const loadMore = useCallback(() => setLimit((n) => Math.min(n + MARKETS_PAGE_SIZE, MARKETS_MAX_LIMIT)), [])
  return { markets, outrights, mySlips, slipQuotes, loadMore, isLive: isOpen }
}

/** I mercati di un gruppo di una partita, le quote della schedina e le schedine dell'ospite, riletti a ogni evento live (timer compreso). */
export function useZambeBetMatch(tournamentId: string | undefined, matchId: string | undefined, group: MarketGroup, guestToken: string | undefined, slipLegKeys: string[] = []) {
  const [version, setVersion] = useState(0)
  const { isOpen } = useLive(tournamentId, () => setVersion((v) => v + 1))
  const base = tournamentId ? `${API_ROUTES.tournaments}/${tournamentId}` : null
  const detail = useApiResource<ZambeBetMatchResponse>(base && matchId ? `${base}/zambebet/matches/${matchId}?group=${group}` : null, guestToken, version, remembered(tournamentId, guestToken))
  const slipQuotes = useApiResource<ZambeBetQuotesResponse>(quotesPath(base, slipLegKeys), undefined, version)
  // Le schedine dell'ospite servono alla barra delle giocate live in attesa, anche su questa pagina.
  const mySlips = useApiResource<SlipsMineResponse>(base && guestToken ? `${base}/slips/mine` : null, guestToken, version)
  return { detail, slipQuotes, mySlips, isLive: isOpen }
}
